import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { FileText, Loader2, AlertCircle, MapPin, X } from 'lucide-react';
import { useAppSelector } from '../../redux/hooks';
import { useGetSiteArtefactsQuery, useGetArtefactViewUrlMutation, type Artefact } from '../../redux/api/portfolioApi';

// PDF Viewer imports
import { Viewer, Worker, SpecialZoomLevel } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import { pageNavigationPlugin } from '@react-pdf-viewer/page-navigation';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import '@react-pdf-viewer/page-navigation/lib/styles/index.css';

interface PageRef {
  label: string;
  page: number;
  type?: string;
}

interface SelectedComponentInfo {
  nodeId: string;
  nodeLabel?: string;
  pageRefs: PageRef[];
}

const AsBuiltViewerTab: React.FC = () => {
  const [viewerError, setViewerError] = useState<string | null>(null);
  const [pdfViewUrl, setPdfViewUrl] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(0); // 0-indexed for the viewer
  const [totalPages, setTotalPages] = useState(0);
  const [pageRefs, setPageRefs] = useState<PageRef[]>([]);
  const [selectedComponent, setSelectedComponent] = useState<SelectedComponentInfo | null>(null);

  // Ref to store the jumpToPage function from the plugin
  const jumpToPageRef = useRef<((pageIndex: number) => void) | null>(null);
  const goToPageRef = useRef<(page: number) => void>(() => {});
  const pendingNavigationRef = useRef<number | null>(null); // Store page to navigate to when PDF loads

  // Get current site from Redux
  const currentSite = useAppSelector((state) => state.site.currentSite);
  const tenantId = currentSite?.tenant_id;
  const siteId = currentSite?.site_id || currentSite?.id;

  // Fetch artefacts for the site
  const {
    data: artefactsData,
    isLoading: isLoadingArtefacts,
    error: artefactsError,
  } = useGetSiteArtefactsQuery(
    { tenant_id: tenantId!, site_id: siteId! },
    { skip: !tenantId || !siteId }
  );

  // Mutation to get the view URL
  const [getViewUrl, { isLoading: isLoadingViewUrl }] = useGetArtefactViewUrlMutation();

  // Find the first artefact with processing_backend === 'cloud_tasks'
  const asBuiltArtefact = useMemo<Artefact | null>(() => {
    if (!artefactsData?.items) return null;

    return artefactsData.items.find(
      (item) => item.meta?.processing_backend === 'cloud_tasks'
    ) || null;
  }, [artefactsData]);

  // Create the page navigation plugin for programmatic page jumping
  // Using useRef to keep the same instance across renders
  const pageNavigationPluginRef = useRef(pageNavigationPlugin());
  const pageNavigationPluginInstance = pageNavigationPluginRef.current;
  const { jumpToPage } = pageNavigationPluginInstance;

  // Store jumpToPage in ref so it can be used in callbacks
  // Also execute pending navigation if jumpToPage becomes available
  useEffect(() => {
    jumpToPageRef.current = jumpToPage;

    // If there's a pending navigation and jumpToPage is now available, execute it
    if (jumpToPage && pendingNavigationRef.current !== null) {
      const pendingPage = pendingNavigationRef.current;
      pendingNavigationRef.current = null;

      let page = pendingPage;
      if (page < 1) page = 1;
      if (totalPages > 0 && page > totalPages) page = totalPages;
      const pageIndex = page - 1;
      jumpToPage(pageIndex);
      setCurrentPage(pageIndex);
    }
  }, [jumpToPage, totalPages]);

  // Create the default layout plugin (keep same instance across renders)
  const defaultLayoutPluginRef = useRef(defaultLayoutPlugin({
    sidebarTabs: () => [], // Hide sidebar
    toolbarPlugin: {
      fullScreenPlugin: {
        onEnterFullScreen: () => {},
        onExitFullScreen: () => {},
      },
    },
  }));
  const defaultLayoutPluginInstance = defaultLayoutPluginRef.current;

  // Extract page refs from artefact metadata if available
  useEffect(() => {
    if (asBuiltArtefact?.meta?.page_refs) {
      setPageRefs(asBuiltArtefact.meta.page_refs as PageRef[]);
    } else if (asBuiltArtefact?.meta?.components) {
      const components = asBuiltArtefact.meta.components as Array<{ label: string; page: number; type?: string }>;
      setPageRefs(components.map(c => ({
        label: c.label,
        page: c.page,
        type: c.type,
      })));
    } else {
      setPageRefs([]);
    }
  }, [asBuiltArtefact]);

  // Fetch the view URL when we have an artefact
  useEffect(() => {
    if (!asBuiltArtefact || !tenantId) {
      setPdfViewUrl(null);
      return;
    }

    const fetchViewUrl = async () => {
      try {
        setViewerError(null);
        const result = await getViewUrl({
          document_id: asBuiltArtefact.id,
          tenant_id: tenantId,
          expires_in: 3600,
        }).unwrap();

        setPdfViewUrl(result.url);
      } catch (err) {
        console.error('Failed to get view URL:', err);
        setViewerError('Failed to get document URL');
      }
    };

    fetchViewUrl();
  }, [asBuiltArtefact, tenantId, getViewUrl]);

  // Go to a specific page (1-indexed input, converts to 0-indexed for viewer)
  const goToPage = useCallback((page: number) => {
    if (page < 1) page = 1;
    if (totalPages > 0 && page > totalPages) page = totalPages;

    const pageIndex = page - 1; // Convert to 0-indexed
    setCurrentPage(pageIndex);

    // Use the jump to page function if available
    if (jumpToPageRef.current) {
      jumpToPageRef.current(pageIndex);
    } else {
      // Store as pending navigation to execute when PDF loads
      pendingNavigationRef.current = page;
    }
  }, [totalPages]);

  // Keep goToPageRef updated
  useEffect(() => {
    goToPageRef.current = goToPage;
  }, [goToPage]);

  // Listen for component selection events from other parts of the app
  useEffect(() => {
    const handleComponentSelect = (event: CustomEvent<{
      page?: number;
      label?: string;
      nodeId?: string;
      nodeLabel?: string;
      pageRefs?: PageRef[];
    }>) => {
      const detail = event.detail;

      if (detail?.nodeId) {
        const refs: PageRef[] = [];

        if (Array.isArray(detail.pageRefs) && detail.pageRefs.length > 0) {
          refs.push(...detail.pageRefs);
        } else if (detail.page != null) {
          refs.push({
            label: detail.nodeLabel || detail.nodeId,
            page: detail.page,
          });
        }

        setSelectedComponent({
          nodeId: detail.nodeId,
          nodeLabel: detail.nodeLabel,
          pageRefs: refs,
        });

        // Navigate to the first page
        if (refs.length > 0) {
          goToPageRef.current(refs[0].page);
        } else if (detail.page != null) {
          goToPageRef.current(detail.page);
        }
      } else if (detail?.page != null) {
        goToPageRef.current(detail.page);
      } else if (detail?.label && pageRefs.length > 0) {
        const ref = pageRefs.find(r => r.label === detail.label);
        if (ref) {
          goToPageRef.current(ref.page);
        }
      }
    };

    window.addEventListener('navigateToAsBuiltPage', handleComponentSelect as EventListener);
    return () => {
      window.removeEventListener('navigateToAsBuiltPage', handleComponentSelect as EventListener);
    };
  }, [pageRefs]);

  const clearSelectedComponent = useCallback(() => {
    setSelectedComponent(null);
  }, []);

  // Handle page change from the viewer
  const handlePageChange = (e: { currentPage: number }) => {
    setCurrentPage(e.currentPage);
  };

  // Handle document load to get total pages and execute pending navigation
  const handleDocumentLoad = (e: { doc: { numPages: number } }) => {
    setTotalPages(e.doc.numPages);

    // Execute pending navigation after a short delay to ensure viewer is ready
    if (pendingNavigationRef.current !== null) {
      const pendingPage = pendingNavigationRef.current;
      pendingNavigationRef.current = null;

      // Use setTimeout to allow the viewer to fully initialize
      setTimeout(() => {
        if (jumpToPageRef.current) {
          let page = pendingPage;
          if (page < 1) page = 1;
          if (page > e.doc.numPages) page = e.doc.numPages;
          const pageIndex = page - 1;
          jumpToPageRef.current(pageIndex);
          setCurrentPage(pageIndex);
        }
      }, 100);
    }
  };

  const isLoading = isLoadingArtefacts || isLoadingViewUrl;

  // No site selected
  if (!currentSite || !tenantId || !siteId) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-engramiq-dark-blue">
        <div className="text-center p-8">
          <FileText className="w-12 h-12 text-engramiq-bright-grey mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-engramiq-light-grey mb-2">No Site Selected</h2>
          <p className="text-engramiq-bright-grey">
            Please select a site from the Sites List to view as-built drawings.
          </p>
        </div>
      </div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-engramiq-dark-blue">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-engramiq-green animate-spin mx-auto mb-4" />
          <p className="text-engramiq-light-grey">Loading as-built drawings...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (artefactsError) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-engramiq-dark-blue">
        <div className="text-center p-8">
          <AlertCircle className="w-12 h-12 text-accent-red mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-engramiq-light-grey mb-2">Error Loading Artefacts</h2>
          <p className="text-engramiq-bright-grey">
            Failed to load as-built drawings. Please try again later.
          </p>
        </div>
      </div>
    );
  }

  // No as-built found
  if (!asBuiltArtefact) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-engramiq-dark-blue">
        <div className="text-center p-8">
          <FileText className="w-12 h-12 text-engramiq-bright-grey mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-engramiq-light-grey mb-2">No As-Built Drawing Found</h2>
          <p className="text-engramiq-bright-grey">
            No processed as-built drawing is available for this site.
          </p>
        </div>
      </div>
    );
  }

  const displayPage = currentPage + 1; // Convert to 1-indexed for display

  return (
    <div className="w-full h-full bg-engramiq-dark-blue flex flex-col">
      {/* Selected Component Page Refs Indicator - compact, only shown when a component is selected */}
      {selectedComponent && selectedComponent.pageRefs.length > 0 && (
        <div className="px-2 py-1 bg-engramiq-green/10 border-b border-engramiq-green/30 flex items-center gap-2">
          <MapPin className="w-3.5 h-3.5 text-engramiq-green flex-shrink-0" />
          <span className="text-xs text-engramiq-light-grey font-medium truncate max-w-[150px]">
            {selectedComponent.nodeLabel || selectedComponent.nodeId}:
          </span>
          <div className="flex items-center gap-1.5 flex-wrap">
            {selectedComponent.pageRefs.map((ref, idx) => (
              <button
                key={idx}
                onClick={() => goToPage(ref.page)}
                className={`px-2 py-0.5 text-xs rounded-full transition-colors ${
                  displayPage === ref.page
                    ? 'bg-engramiq-green text-engramiq-dark-blue font-semibold'
                    : 'bg-surface-700 text-engramiq-light-grey hover:bg-surface-600'
                }`}
              >
                p{ref.page}
              </button>
            ))}
          </div>
          <button
            onClick={clearSelectedComponent}
            className="ml-auto p-0.5 text-engramiq-bright-grey hover:text-engramiq-light-grey hover:bg-surface-700 rounded"
            title="Clear selection"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* PDF Viewer - Full height with dark theme */}
      <div className="flex-1 w-full relative overflow-hidden">
        {viewerError ? (
          <div className="absolute inset-0 flex items-center justify-center bg-surface-900">
            <div className="text-center">
              <AlertCircle className="w-8 h-8 text-accent-red mx-auto mb-2" />
              <p className="text-engramiq-light-grey mb-1">Cannot Display Document</p>
              <p className="text-engramiq-bright-grey text-sm mb-3">{viewerError}</p>
            </div>
          </div>
        ) : pdfViewUrl ? (
          <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
            <div className="h-full pdf-viewer-dark
              [&_.rpv-core__viewer]:h-full
              [&_.rpv-core__inner-container]:!bg-[#1a1f2e]
              [&_.rpv-default-layout__container]:!bg-[#1a1f2e]
              [&_.rpv-default-layout__toolbar]:!bg-[#0d1830]
              [&_.rpv-default-layout__toolbar]:!border-b
              [&_.rpv-default-layout__toolbar]:!border-engramiq-green/20
              [&_.rpv-core__minimal-button]:!text-engramiq-light-grey
              [&_.rpv-core__minimal-button:hover]:!bg-surface-700
              [&_.rpv-core__textbox]:!bg-surface-800
              [&_.rpv-core__textbox]:!text-engramiq-light-grey
              [&_.rpv-core__textbox]:!border-engramiq-green/30
              [&_.rpv-zoom__popover-target-scale]:!text-engramiq-light-grey
              [&_.rpv-page-navigation__current-page-input]:!bg-surface-800
              [&_.rpv-page-navigation__current-page-input]:!text-engramiq-light-grey
            ">
              <Viewer
                fileUrl={pdfViewUrl}
                plugins={[defaultLayoutPluginInstance, pageNavigationPluginInstance]}
                defaultScale={SpecialZoomLevel.PageWidth}
                onPageChange={handlePageChange}
                onDocumentLoad={handleDocumentLoad}
                initialPage={currentPage}
                theme="dark"
                renderError={() => (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <AlertCircle className="w-8 h-8 text-accent-red mx-auto mb-2" />
                      <p className="text-engramiq-light-grey">Failed to load PDF</p>
                    </div>
                  </div>
                )}
              />
            </div>
          </Worker>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <Loader2 className="w-8 h-8 text-engramiq-green animate-spin" />
          </div>
        )}
      </div>
    </div>
  );
};

export default AsBuiltViewerTab;
